import { Tooltip } from "@mui/material";
import { ReactElement } from "react";

import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { displayStatus } from "~/utils/statuses";

export type StatusLogEntry = {
  event: string;
  eventTime: string;
  autoGenerated: boolean;
  createdByUser: { firstName: string; lastName: string } | null;
};

export const buildStatusLine = ({
  status,
  logEntry,
  formatTime,
}: {
  status: string;
  logEntry: StatusLogEntry | undefined;
  formatTime: (eventTime: string) => string;
}): string | undefined => {
  if (!logEntry) {
    const fallback = displayStatus(status, "visit");
    return fallback === "Scheduled" ? undefined : fallback;
  }

  const setBy = logEntry.autoGenerated
    ? "System"
    : `${logEntry.createdByUser?.firstName?.[0] ?? ""}${logEntry.createdByUser?.lastName?.[0] ?? ""}`.toUpperCase();

  return [displayStatus(logEntry.event, "visit"), setBy, formatTime(logEntry.eventTime)]
    .filter((part) => !!part)
    .join(" ");
};

type Props = {
  title: string;
  status: string;
  logEntry: StatusLogEntry | undefined;
  /** Hides this tooltip while a more specific tooltip inside the event is showing. */
  suppressed?: boolean;
  children: ReactElement;
};

const VisitStatusTooltip = ({ title, status, logEntry, suppressed = false, children }: Props) => {
  const { timeLabel } = useZonedDateFormat();
  const statusLine = buildStatusLine({
    status,
    logEntry,
    formatTime: (eventTime) => timeLabel(eventTime),
  });

  // Emptying the title rather than controlling `open` leaves MUI's own hover tracking intact, so the tooltip
  // reappears if the pointer is still on the event once the inner tooltip closes, and stays hidden if it isn't.
  return (
    <Tooltip
      title={
        suppressed ? "" : (
          statusLine ? (
            <>
              {title}
              <br />
              {statusLine}
            </>
          ) : (
            title
          )
        )
      }
      placement="top"
      arrow
      enterDelay={400}
      disableInteractive
    >
      {children}
    </Tooltip>
  );
};

export default VisitStatusTooltip;
